import { useEffect, useMemo, useRef, useState } from 'react'
import Form from './components/Form'
import MemoryCard from './components/MemoryCard'
import { data as staticData } from './data/data'

const CATEGORY_LABELS = {
  flags: '🎌 Flags',
  'travel-and-places': '🧳 Travel',
}

const STATS_KEY = 'memory-game-stats'

const WIN_MODAL_DELAY_MS = 2_000

function statsStorageKey(category, pairs) {
  return `${category}:${pairs}`
}

function loadGameStats() {
  try {
    const raw = localStorage.getItem(STATS_KEY)
    if (!raw) return { last: null, bestByKey: {} }
    const parsed = JSON.parse(raw)
    return {
      last: parsed.last ?? null,
      bestByKey: typeof parsed.bestByKey === 'object' && parsed.bestByKey ? parsed.bestByKey : {},
    }
  } catch {
    return { last: null, bestByKey: {} }
  }
}

function persistWinResult(moves, category, pairs) {
  try {
    const prev = loadGameStats()
    const key = statsStorageKey(category, pairs)
    const at = new Date().toISOString()
    const last = { moves, pairs, category, at }
    const prevBest = prev.bestByKey[key]
    const nextBest =
      prevBest == null || moves < prevBest.moves ? { moves, at } : prevBest
    localStorage.setItem(
      STATS_KEY,
      JSON.stringify({
        last,
        bestByKey: { ...prev.bestByKey, [key]: nextBest },
      }),
    )
    return loadGameStats()
  } catch {
    return loadGameStats()
  }
}

function makeId() {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`
  }
}

function shuffleInPlace(arr) {
  // Fisher–Yates shuffle
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = arr[i]
    arr[i] = arr[j]
    arr[j] = temp
  }
  return arr
}

function sampleUnique(items, count) {
  const result = []
  const used = new Set()
  const max = Math.min(count, items.length)

  while (result.length < max) {
    const idx = Math.floor(Math.random() * items.length)
    if (used.has(idx)) continue
    used.add(idx)
    result.push(items[idx])
  }
  return result
}

export default function App() {
  const MAX_PAIRS = 10
  const [status, setStatus] = useState('idle') // idle | loading | playing | won | error
  const [gameStats, setGameStats] = useState(loadGameStats)
  const [options, setOptions] = useState({
    category: 'flags',
    pairs: 4,
  })

  const [deck, setDeck] = useState([]) // { id, name, symbol, isMatched }
  const [selectedIds, setSelectedIds] = useState([]) // [id, id]
  const [isLocked, setIsLocked] = useState(false)
  const [moves, setMoves] = useState(0)

  const resolveTimeoutRef = useRef(null)
  const winModalTimeoutRef = useRef(null)
  const [showWinModal, setShowWinModal] = useState(false)

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  useEffect(() => {
    return () => {
      if (resolveTimeoutRef.current) clearTimeout(resolveTimeoutRef.current)
      if (winModalTimeoutRef.current) clearTimeout(winModalTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (status !== 'won') {
      setShowWinModal(false)
      if (winModalTimeoutRef.current) {
        clearTimeout(winModalTimeoutRef.current)
        winModalTimeoutRef.current = null
      }
      return
    }
    setShowWinModal(false)
    winModalTimeoutRef.current = setTimeout(() => {
      setShowWinModal(true)
      winModalTimeoutRef.current = null
    }, WIN_MODAL_DELAY_MS)
    return () => {
      if (winModalTimeoutRef.current) {
        clearTimeout(winModalTimeoutRef.current)
        winModalTimeoutRef.current = null
      }
    }
  }, [status])

  useEffect(() => {
    if (deck.length === 0) return
    const allMatched = deck.every((c) => c.isMatched)
    if (allMatched) {
      setStatus('won')
      setIsLocked(false)
      setSelectedIds([])
      if (resolveTimeoutRef.current) clearTimeout(resolveTimeoutRef.current)
    }
  }, [deck])

  useEffect(() => {
    if (status !== 'won') return
    setGameStats(persistWinResult(moves, options.category, options.pairs))
  }, [status, moves, options.category, options.pairs])

  function startGame(nextOptions) {
    if (resolveTimeoutRef.current) clearTimeout(resolveTimeoutRef.current)
    if (winModalTimeoutRef.current) {
      clearTimeout(winModalTimeoutRef.current)
      winModalTimeoutRef.current = null
    }
    setShowWinModal(false)

    const category = nextOptions?.category ?? options.category
    const pairs = Number(nextOptions?.pairs ?? options.pairs)
    const normalizedPairs = Number.isFinite(pairs) ? Math.floor(pairs) : 4
    const safePairs = Math.max(2, Math.min(MAX_PAIRS, normalizedPairs))

    setOptions({ category, pairs: safePairs })
    setStatus('loading')
    setIsLocked(true)
    setMoves(0)
    setSelectedIds([])
    setDeck([])

    try {
      if (category === 'flags') {
        const sampled = sampleUnique(staticData.flagCountries, safePairs)
        if (sampled.length < safePairs) {
          throw new Error('Not enough flags available for this difficulty.')
        }

        const base = sampled.map((c) => ({
          name: c.name,
          type: 'flag',
          code: c.code,
          capital: c.capital ?? '',
        }))

        const nextDeck = shuffleInPlace(
          [...base, ...base].map((card) => ({
            id: makeId(),
            name: card.name,
            type: card.type,
            code: card.code,
            capital: card.capital ?? '',
            symbol: null,
            isMatched: false,
          })),
        )

        setDeck(nextDeck)
        setStatus('playing')
        setIsLocked(false)
        return
      }

      if (category === 'travel-and-places') {
        const sampled = sampleUnique(staticData.sightseeing, safePairs)
        if (sampled.length < safePairs) {
          throw new Error('Not enough sightseeing cards for this difficulty.')
        }

        const base = sampled.map((item) => ({
          name: item.landmark,
          type: 'sightseeing',
          landmark: item.landmark,
          city: item.city,
          image: item.image,
        }))

        const nextDeck = shuffleInPlace(
          [...base, ...base].map((card) => ({
            id: makeId(),
            name: card.name,
            type: card.type,
            landmark: card.landmark,
            city: card.city,
            image: card.image,
            symbol: null,
            isMatched: false,
          })),
        )

        setDeck(nextDeck)
        setStatus('playing')
        setIsLocked(false)
        return
      }

      throw new Error('Unknown category.')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error fetching data', e)
      setStatus('error')
      setIsLocked(false)
    }
  }

  function restartGame() {
    startGame(options)
  }

  function goHome() {
    if (resolveTimeoutRef.current) clearTimeout(resolveTimeoutRef.current)
    if (winModalTimeoutRef.current) {
      clearTimeout(winModalTimeoutRef.current)
      winModalTimeoutRef.current = null
    }
    setShowWinModal(false)
    setStatus('idle')
    setDeck([])
    setMoves(0)
    setSelectedIds([])
    setIsLocked(false)
  }

  function turnCard(cardId) {
    if (status !== 'playing') return
    if (isLocked) return
    if (selectedSet.has(cardId)) return

    const clicked = deck.find((c) => c.id === cardId)
    if (!clicked || clicked.isMatched) return

    if (selectedIds.length === 0) {
      setSelectedIds([cardId])
      return
    }

    if (selectedIds.length === 1) {
      const firstId = selectedIds[0]
      const secondId = cardId

      setSelectedIds([firstId, secondId])
      setMoves((m) => m + 1)
      setIsLocked(true)

      const first = deck.find((c) => c.id === firstId)
      const second = clicked
      const isMatch = first && second && first.name === second.name

      if (isMatch) {
        setDeck((prev) =>
          prev.map((c) =>
            c.id === firstId || c.id === secondId ? { ...c, isMatched: true } : c,
          ),
        )
        setSelectedIds([])
        setIsLocked(false)
        return
      }

      resolveTimeoutRef.current = setTimeout(() => {
        setSelectedIds([])
        setIsLocked(false)
      }, 850)
    }
  }

  const isGameOn = status === 'loading' || status === 'playing' || status === 'won'

  return (
    <main className={isGameOn ? 'main--playing' : undefined}>
      {!isGameOn && (
        <Form
          defaultCategory={options.category}
          defaultPairs={options.pairs}
          gameStats={gameStats}
          handleSubmit={startGame}
          status={status}
        />
      )}

      {isGameOn && (
        <div className="game-screen">
          <header className="game-header">
            <h1 className="game-header__title">Memory Match</h1>
          </header>

          <section className="game-info-card" aria-label="Game progress and actions">
            <div className="game-info-card__stats">
              <p className="p--regular game-info-card__stat">
                <strong>Category:</strong>{' '}
                {CATEGORY_LABELS[options.category] ?? options.category}
              </p>
              <p className="p--regular game-info-card__stat">
                <strong>Moves:</strong> {moves}
              </p>
            </div>
            <div className="game-info-card__actions">
              <button className="btn btn--home" type="button" onClick={goHome}>
                Home
              </button>
              <button className="btn btn--restart" type="button" onClick={restartGame}>
                Restart
              </button>
            </div>
          </section>

          {status === 'error' && (
            <p className="p--regular game-error">Couldn’t start the game. Try again.</p>
          )}

          <section className="game-grid-section" aria-label="Card grid">
            <MemoryCard
              data={deck}
              handleClick={turnCard}
              selectedIds={selectedSet}
            />
          </section>

          {status === 'won' && showWinModal && (
            <div className="overlay overlay--fade-in" role="status" aria-live="polite">
              <div className="win wrapper wrapper--accent">
                <p className="p--large">You won!</p>
                <p className="p--regular">
                  Finished in <strong>{moves}</strong> moves.
                </p>
                <div className="win__actions">
                  <button className="btn btn--restart" type="button" onClick={restartGame}>
                    Play again
                  </button>
                  <button className="btn btn--home" type="button" onClick={goHome}>
                    Home
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  )
}
